import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaLinkedin as Linkedin } from "react-icons/fa";
import PageShell, { SectionLabel } from "@/components/PageShell";
import SEO from "@/components/SEO";
import { getTeam } from "@/lib/api";

export default function Team() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTeam()
      .then(setMembers)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageShell testId="page-team">
      <SEO
        title="Team — Imaginepedia Technologies"
        description="Get to know the people working behind the scenes making everything possible."
        path="/"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-16 md:py-24">
          <div className="md:col-span-4">
            <SectionLabel index={1}>Team</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
              Small crew.<br />
              <span className="text-zinc-500">Deep ownership.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-400 leading-relaxed max-w-2xl">
              The people who ship the work. No account managers between you and the engineers building your system.
            </p>
          </div>
        </div>

        <div className="border-t border-white/5 pt-12 pb-24">
          {loading && (
            <p data-testid="team-loading" className="font-mono text-sm text-zinc-500">
              Loading team…
            </p>
          )}
          <div data-testid="team-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map((m, i) => (
              <motion.div
                key={m.id}
                data-testid={`team-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="group bg-[#0C0C0D] border border-white/5 hover:border-[#CCFF00]/30 transition-colors duration-300"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#050505]">
                  <img
                    src={m.image_url}
                    alt={m.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter,transform] duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="absolute top-4 right-4 w-2 h-2 bg-[#CCFF00] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-display text-xl text-white">{m.name}</h3>
                      <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#CCFF00] mt-1">
                        {m.role}
                      </p>
                    </div>
                    {m.linkedin && (
                      <a
                        href={m.linkedin}
                        aria-label={`${m.name} on LinkedIn`}
                        className="text-zinc-500 hover:text-[#CCFF00] transition-colors"
                      >
                        <Linkedin size={16} />
                      </a>
                    )}
                  </div>
                  <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{m.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
